
import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";

import {
  loginUser,
  clearError,
} from "../../redux/slices/authSlice";

import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaUserMd,
  FaUserInjured,
  FaArrowRight,
  FaShieldAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaUserPlus,
  FaStethoscope,
} from "react-icons/fa";

import {
  motion,
  AnimatePresence,
  useReducedMotion,
} from "motion/react";

import logo from "../../Pages/Header/logo.png";

/* =============================================================
   MRHEALTH BRAND GRADIENT
   ============================================================= */

const MRHEALTH_GRADIENT =
  "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)";

/* =============================================================
   MOTION VARIANTS
   ============================================================= */

const leftPanelVariants = {
  hidden: {
    opacity: 0,
    x: -35,
  },

  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const rightPanelVariants = {
  hidden: {
    opacity: 0,
    x: 35,
  },

  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 1.2,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const heroContainerVariants = {
  hidden: {},

  visible: {
    transition: {
      delayChildren: 0.35,
      staggerChildren: 0.18,
    },
  },
};

const heroItemVariants = {
  hidden: {
    opacity: 0,
    y: 18,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const formContainerVariants = {
  hidden: {},

  visible: {
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.12,
    },
  },
};

const formItemVariants = {
  hidden: {
    opacity: 0,
    y: 12,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const alertVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.98,
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },

  exit: {
    opacity: 0,
    y: -8,
    scale: 0.98,
    transition: {
      duration: 0.3,
    },
  },
};

const roleIconVariants = {
  initial: {
    opacity: 0,
    scale: 0.5,
    rotate: -15,
  },

  animate: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },

  exit: {
    opacity: 0,
    scale: 0.5,
    rotate: 15,
    transition: {
      duration: 0.3,
    },
  },
};

/* =============================================================
   COMPONENT
   ============================================================= */

const Login = () => {
  /* ===========================================================
     STATE
     =========================================================== */

  const [username, setUsername] = useState("");

  const [password, setPassword] = useState("");

  /*
   * user     = Patient
   * business = Doctor / Other Medical / Alternative Medical
   */
  const [role, setRole] = useState("user");

  const [showPassword, setShowPassword] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");

  const [infoMessage, setInfoMessage] = useState("");

  /* ===========================================================
     REDIRECT TIMER REFS
     =========================================================== */

  const redirectTimerRef = useRef(null);

  const successTimerRef = useRef(null);

  /* ===========================================================
     REDUX / ROUTER
     =========================================================== */

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const location = useLocation();

  const { loading } = useSelector(
    (state) => state.auth
  );

  /* ===========================================================
     REDUCED MOTION
     =========================================================== */

  const shouldReduceMotion = useReducedMotion();

  /* ===========================================================
     CLEANUP TIMERS
     =========================================================== */

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }

      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  /* ===========================================================
     SUCCESS MESSAGE FROM SIGNUP
     =========================================================== */

  useEffect(() => {
    const success = location.state?.success;

    if (!success) {
      return;
    }

    setSuccessMessage(success);

    setInfoMessage("");

    dispatch(clearError());

    navigate(location.pathname, {
      replace: true,
      state: {},
    });

    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }

    successTimerRef.current = setTimeout(() => {
      setSuccessMessage("");
    }, 5000);

    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, [
    location.state,
    location.pathname,
    navigate,
    dispatch,
  ]);

  /* ===========================================================
     ROLE CHANGE
     =========================================================== */

  const handleRoleChange = (newRole) => {
    if (loading) {
      return;
    }

    setRole(newRole);

    setInfoMessage("");

    setSuccessMessage("");

    dispatch(clearError());
  };

  /* ===========================================================
     GET AVAILABILITY PAGE
     =========================================================== */

  const getAvailabilityPath = (categoryPath) => {
    const primaryCategory = String(
      categoryPath?.[0] ?? ""
    )
      .toLowerCase()
      .trim();

    /*
     * IMPORTANT:
     * Category is NOT selected from the login page.
     * It comes from the logged-in business user's
     * categoryPath.
     */

    if (primaryCategory.includes("alternative")) {
      return "/availabilities/alternative";
    }

    if (primaryCategory.includes("doctor")) {
      return "/availabilities/doctor";
    }

    if (primaryCategory.includes("other medical")) {
      return "/availabilities/other-medical";
    }

    return "/availabilities";
  };

  /* ===========================================================
     GO TO AVAILABILITY
     =========================================================== */

  const goToAvailability = (categoryPath) => {
    const path = getAvailabilityPath(categoryPath);

    navigate(path, {
      replace: true,
    });
  };

  /* ===========================================================
     SAFE REDIRECT
     =========================================================== */

  const scheduleRedirect = (callback, delay) => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = setTimeout(() => {
      redirectTimerRef.current = null;

      callback();
    }, delay);
  };

  /* ===========================================================
     HANDLE LOGIN
     =========================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    setInfoMessage("");

    setSuccessMessage("");

    dispatch(clearError());

    /* =========================================================
       VALIDATION
       ========================================================= */

    const trimmedUsername = username.trim();

    if (!trimmedUsername) {
      setInfoMessage(
        "Please enter your username or email."
      );

      return;
    }

    if (!password) {
      setInfoMessage(
        "Please enter your password."
      );

      return;
    }

    /* =========================================================
       LOGIN REQUEST
       ========================================================= */

    try {
      const resultAction = await dispatch(
        loginUser({
          username: trimmedUsername,
          password,
          role,
        })
      );

      /* =======================================================
         LOGIN REJECTED
         ======================================================= */

      if (
        loginUser.rejected.match(resultAction)
      ) {
        const rejectedPayload =
          resultAction.payload ?? {};

        const rejectedError =
          resultAction.error ?? {};

        const redirectPath =
          rejectedPayload?.redirect ??
          rejectedPayload?.response?.data
            ?.redirect ??
          rejectedError?.redirect ??
          rejectedError?.response?.data
            ?.redirect ??
          null;

        const backendMessage =
          rejectedPayload?.message ??
          rejectedPayload?.response?.data
            ?.message ??
          rejectedPayload?.error ??
          rejectedError?.message ??
          "";

        if (redirectPath) {
          setInfoMessage(
            backendMessage ||
              "Redirecting..."
          );

          scheduleRedirect(() => {
            navigate(redirectPath, {
              replace: true,
            });
          }, 500);

          return;
        }

        setInfoMessage(
          backendMessage ||
            "Login failed. Please check your credentials and try again."
        );

        return;
      }

      /* =======================================================
         VERIFY LOGIN SUCCESS
         ======================================================= */

      if (
        !loginUser.fulfilled.match(resultAction)
      ) {
        setInfoMessage(
          "Login could not be completed. Please try again."
        );

        return;
      }

      /* =======================================================
         LOGIN SUCCESS
         ======================================================= */

      const payload = resultAction.payload ?? {};

      if (!payload?.user) {
        setInfoMessage(
          "Login succeeded, but account details could not be loaded. Please try again."
        );

        return;
      }

      const loggedInUser = payload.user;

      /* =======================================================
         USER ROLE
         ======================================================= */

      const userRole = String(
        loggedInUser?.role ?? ""
      )
        .toLowerCase()
        .trim();

      /* =======================================================
         CATEGORY PATH
         ======================================================= */

      const categoryPath = Array.isArray(
        loggedInUser?.categoryPath
      )
        ? loggedInUser.categoryPath
        : [];

      /* =======================================================
         PRIMARY CATEGORY
         ======================================================= */

      const primaryCategory = String(
        categoryPath?.[0] ?? ""
      )
        .toLowerCase()
        .trim();

      /* =======================================================
         AVAILABILITY
         ======================================================= */

      const hasAvailabilitySetup =
        payload?.hasAvailabilitySetup === true;

      const weekStatus =
        payload?.weekStatus ?? null;

      const weeklyAvailability =
        Array.isArray(
          payload?.weeklyAvailability
        )
          ? payload.weeklyAvailability
          : [];

      /* =======================================================
         RESPONSE DATA
         ======================================================= */

      const code = payload?.code ?? null;

      const message = payload?.message ?? "";

      /* =======================================================
         UPCOMING AVAILABILITY
         ======================================================= */

      const hasUpcomingSlots =
        weeklyAvailability.length > 0;

      /* =======================================================
         SUBSCRIPTION EXPIRED
         ======================================================= */

      if (
        code === "SUBSCRIPTION_EXPIRED" ||
        payload?.subscriptionExpired === true
      ) {
        setInfoMessage(
          message ||
            "Your subscription has expired. Redirecting to plans..."
        );

        scheduleRedirect(() => {
          navigate("/select-plan", {
            replace: true,
          });
        }, 1500);

        return;
      }

      /* =======================================================
         PATIENT
         ======================================================= */

      if (userRole === "user") {
        navigate("/dashboard/user", {
          replace: true,
        });

        return;
      }

      /* =======================================================
         HEALTHCARE PROFESSIONAL
         
         business / doctor
         
         Covers:
         - Doctor
         - Other Medical User
         - Alternative Medical User
         ======================================================= */

      if (
        userRole === "business" ||
        userRole === "doctor"
      ) {
        const needsAvailabilityUpdate =
          !hasAvailabilitySetup ||
          weekStatus === "PAST_DATA";

        /* -----------------------------------------------------
           AVAILABILITY NOT SET / EXPIRED
           ----------------------------------------------------- */

        if (needsAvailabilityUpdate) {
          setInfoMessage(
            !hasAvailabilitySetup
              ? "Please set your availability before continuing."
              : "Your previous availability has expired. Please update your availability."
          );

          scheduleRedirect(() => {
            goToAvailability(categoryPath);
          }, 700);

          return;
        }

        /* -----------------------------------------------------
           NO UPCOMING SLOTS
           ----------------------------------------------------- */

        if (!hasUpcomingSlots) {
          setInfoMessage(
            "No slots available for this week. Please update your availability."
          );

          scheduleRedirect(() => {
            goToAvailability(categoryPath);
          }, 1200);

          return;
        }

        /* -----------------------------------------------------
           OTHER MEDICAL SERVICE
           ----------------------------------------------------- */

        if (
          primaryCategory.includes(
            "other medical"
          )
        ) {
          navigate(
            "/dashboard/medical-service",
            {
              replace: true,
            }
          );

          return;
        }

        /* -----------------------------------------------------
           DOCTOR / ALTERNATIVE / OTHER
           BUSINESS DASHBOARD
           ----------------------------------------------------- */

        navigate(
          "/dashboard/business-user",
          {
            replace: true,
          }
        );

        return;
      }

      /* =======================================================
         UNKNOWN ROLE
         ======================================================= */

      setInfoMessage(
        "Unable to determine your account type. Please contact support."
      );
    } catch (err) {
      console.error(
        "Login unexpected error:",
        err
      );

      setInfoMessage(
        "Something went wrong. Please try again."
      );
    }
  };

  /* =============================================================
     UI
     ============================================================= */

  return (
    <main
      className="
        min-h-[100dvh]
        w-full
        overflow-x-hidden
        bg-[#F7FAFC]
      "
    >
      <div
        className="
          min-h-[100dvh]
          w-full
          lg:flex
          lg:items-stretch
        "
      >

        {/* =====================================================
            LEFT BRANDING PANEL
            ===================================================== */}

        <motion.section
          initial={
            shouldReduceMotion
              ? false
              : "hidden"
          }
          animate="visible"
          variants={leftPanelVariants}
          className="
            relative
            hidden
            min-h-[100dvh]
            overflow-hidden
            lg:flex
            lg:w-[48%]
            lg:flex-col
            lg:justify-between
            lg:px-10
            lg:py-8
            xl:w-[50%]
            xl:px-14
            2xl:px-20
          "
          style={{
            background: `
              linear-gradient(
                135deg,
                #071D30 0%,
                #0B2942 38%,
                #103B59 68%,
                #0A2A43 100%
              )
            `,
          }}
        >

          {/* TOP LEFT CIRCLE */}

          <motion.div
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: 0,
                    scale: 0.85,
                  }
            }
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              duration: 1.2,
              delay: 0.25,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            className="
              pointer-events-none
              absolute
              -left-[230px]
              -top-[230px]
              h-[460px]
              w-[460px]
              rounded-full
              border-[1px]
              border-[#4FD1FF]/20
              bg-[#08A8E8]/[0.08]
            "
          />

          {/* TOP LEFT INNER CIRCLE */}

          <motion.div
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: 0,
                    scale: 0.8,
                  }
            }
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              duration: 1.4,
              delay: 0.4,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            className="
              pointer-events-none
              absolute
              -left-[145px]
              -top-[145px]
              h-[290px]
              w-[290px]
              rounded-full
              border-[1px]
              border-[#6DDAFF]/15
              bg-[#08A8E8]/[0.05]
            "
          />

          {/* BOTTOM RIGHT CIRCLE */}

          <motion.div
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: 0,
                    scale: 0.85,
                  }
            }
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              duration: 1.3,
              delay: 0.35,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            className="
              pointer-events-none
              absolute
              -bottom-[260px]
              -right-[260px]
              h-[520px]
              w-[520px]
              rounded-full
              border-[1px]
              border-[#4FD1FF]/20
              bg-[#08A8E8]/[0.07]
            "
          />

          {/* BOTTOM RIGHT INNER CIRCLE */}

          <motion.div
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: 0,
                    scale: 0.8,
                  }
            }
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              duration: 1.5,
              delay: 0.5,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            className="
              pointer-events-none
              absolute
              -bottom-[165px]
              -right-[165px]
              h-[330px]
              w-[330px]
              rounded-full
              border-[1px]
              border-[#6DDAFF]/15
              bg-[#08A8E8]/[0.04]
            "
          />

          {/* CENTER LINE */}

          <div
            className="
              pointer-events-none
              absolute
              inset-y-0
              right-0
              w-px
              bg-gradient-to-b
              from-transparent
              via-[#4FD1FF]/25
              to-transparent
            "
          />

          {/* BRAND CONTENT */}

          <div
            className="
              relative
              z-10
              mx-auto
              flex
              min-h-[430px]
              w-full
              max-w-xl
              flex-col
              lg:h-full
              lg:min-h-0
            "
          >

            {/* LOGO */}

            <button
              type="button"
              onClick={() =>
                navigate("/Home")
              }
              className="
                flex
                shrink-0
                items-center
              "
            >
              <img
                src="/logo.jpeg"
                alt="MR Health"
                title="MR Health"
                className="
                  h-12
                  w-auto
                  object-contain
                  sm:h-14
                  md:h-16
                "
              />
            </button>

            {/* HERO */}

            <motion.div
              variants={heroContainerVariants}
              initial={
                shouldReduceMotion
                  ? false
                  : "hidden"
              }
              animate="visible"
              className="
                mt-auto
                pb-8
                pt-16
                sm:pt-20
                lg:pb-12
                lg:pt-20
                xl:pb-14
                xl:pt-24
              "
            >

              {/* EYEBROW */}

              <motion.div
                variants={heroItemVariants}
                className="
                  mb-5
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-[#4FD1FF]/25
                  bg-[#08A8E8]/10
                  px-3
                  py-1.5
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                  text-[#6DDAFF]
                  shadow-lg
                  shadow-[#08A8E8]/5
                  backdrop-blur-md
                  sm:text-xs
                  sm:tracking-[0.2em]
                "
              >
                <span
                  className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-[#4FD1FF]
                    shadow-[0_0_10px_rgba(79,209,255,0.8)]
                  "
                />

                Healthcare platform
              </motion.div>

              {/* HEADING */}

              <motion.h1
                variants={heroItemVariants}
                className="
                  max-w-[600px]
                  font-serif
                  text-[34px]
                  font-bold
                  leading-[1.12]
                  tracking-[-0.02em]
                  text-white
                  sm:text-[40px]
                  md:text-[46px]
                  lg:text-[42px]
                  xl:text-[52px]
                  2xl:text-[58px]
                "
              >
                Connect with the
                <br />
                right healthcare
                <br />

                <span
                  className="
                    bg-gradient-to-r
                    from-[#4FD1FF]
                    via-[#7DE3FF]
                    to-[#38BDF8]
                    bg-clip-text
                    text-transparent
                  "
                >
                  at the right time.
                </span>
              </motion.h1>

              {/* DESCRIPTION */}

              <motion.p
                variants={heroItemVariants}
                className="
                  mt-6
                  max-w-[520px]
                  text-sm
                  leading-6
                  text-[#B5CBDC]
                  sm:mt-7
                  sm:text-base
                  sm:leading-7
                  md:max-w-[540px]
                  md:text-lg
                  md:leading-8
                "
              >
                Streamlined appointment
                booking for patients and
                comprehensive schedule
                management for doctors and
                healthcare professionals.
              </motion.p>

              {/* TRUST */}

              <motion.div
                variants={heroItemVariants}
                className="
                  mt-8
                  flex
                  flex-wrap
                  items-center
                  gap-x-6
                  gap-y-3
                  text-xs
                  text-[#9CB8CC]
                  sm:text-sm
                "
              >
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-[#4FD1FF]" />
                  Secure platform
                </div>

                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-[#4FD1FF]" />
                  Easy scheduling
                </div>

                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-[#4FD1FF]" />
                  Healthcare focused
                </div>
              </motion.div>
            </motion.div>

            {/* FOOTER */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? false
                  : {
                      opacity: 0,
                    }
              }
              animate={{
                opacity: 1,
              }}
              transition={{
                duration: 0.8,
                delay: 1.2,
              }}
              className="
                hidden
                text-xs
                text-[#7899B0]
                lg:block
              "
            >
              ©{" "}
              {new Date().getFullYear()}{" "}
              MRHealth
              {" · "}mrhealth.co.in
            </motion.div>
          </div>
        </motion.section>

        {/* =====================================================
            RIGHT LOGIN PANEL
            ===================================================== */}

        <motion.section
          initial={
            shouldReduceMotion
              ? false
              : "hidden"
          }
          animate="visible"
          variants={rightPanelVariants}
          className="
            relative
            flex
            min-h-[100dvh]
            w-full
            items-start
            justify-center
            bg-[#F7FAFC]
            px-4
            py-6
            sm:px-6
            sm:py-8
            md:px-10
            md:py-10
            lg:min-h-[100dvh]
            lg:w-[52%]
            lg:items-center
            lg:px-10
            xl:w-[50%]
            xl:px-16
            2xl:px-24
          "
        >

          {/* CORNER DESIGN */}

          <div
            className="
              pointer-events-none
              absolute
              -right-32
              -top-32
              hidden
              h-64
              w-64
              rounded-full
              border
              border-[#08A8E8]/10
              bg-[#08A8E8]/[0.025]
              lg:block
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -bottom-40
              -left-40
              hidden
              h-72
              w-72
              rounded-full
              border
              border-[#08A8E8]/10
              bg-[#08A8E8]/[0.02]
              lg:block
            "
          />

          <div
            className="
              relative
              z-10
              w-full
              max-w-[460px]
            "
          >

            {/* MOBILE BRAND */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? false
                  : {
                      opacity: 0,
                      y: -15,
                    }
              }
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.25,
              }}
              className="
                mb-5
                flex
                justify-center
                sm:mb-7
                lg:hidden
              "
            >
              <Link
                to="/"
                className="
                  inline-flex
                  items-center
                  gap-3
                "
              >
                <motion.div
                  whileTap={
                    shouldReduceMotion
                      ? {}
                      : {
                          scale: 0.94,
                        }
                  }
                  className="
                    relative
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    overflow-hidden
                    rounded-full
                    border
                    border-white/15
                    shadow-lg
                    shadow-black/20
                  "
                  style={{
                    background:
                      MRHEALTH_GRADIENT,
                  }}
                >
                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-0
                      rounded-full
                      bg-white/5
                    "
                  />

                  <img
                    src={logo}
                    alt="MRHealth"
                    className="
                      relative
                      z-10
                      h-full
                      w-full
                      object-contain
                      p-1.5
                      brightness-0
                      invert
                    "
                  />
                </motion.div>

                <span
                  className="
                    text-lg
                    font-bold
                    text-[#102F49]
                  "
                >
                  MRHealth
                </span>
              </Link>
            </motion.div>

            {/* LOGIN HEADER */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? false
                  : {
                      opacity: 0,
                      y: 20,
                    }
              }
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.35,
              }}
              className="text-center"
            >
              <motion.div
                whileHover={
                  shouldReduceMotion
                    ? {}
                    : {
                        scale: 1.05,
                        rotate: 2,
                      }
                }
                className="
                  mx-auto
                  mb-3
                  flex
                  h-12
                  w-12
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[#E6F7FC]
                  text-[#08A8E8]
                  shadow-sm
                  sm:mb-5
                  sm:h-16
                  sm:w-16
                "
              >
                <AnimatePresence
                  mode="wait"
                  initial={false}
                >
                  <motion.div
                    key={role}
                    variants={roleIconVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    {role === "business" ? (
                      <FaUserMd className="text-2xl sm:text-3xl" />
                    ) : (
                      <FaUserInjured className="text-2xl sm:text-3xl" />
                    )}
                  </motion.div>
                </AnimatePresence>
              </motion.div>

              <AnimatePresence
                mode="wait"
                initial={false}
              >
                <motion.h2
                  key={role}
                  initial={
                    shouldReduceMotion
                      ? false
                      : {
                          opacity: 0,
                          y: 8,
                        }
                  }
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={
                    shouldReduceMotion
                      ? {}
                      : {
                          opacity: 0,
                          y: -8,
                        }
                  }
                  transition={{
                    duration: 0.4,
                  }}
                  className="
                    font-serif
                    text-2xl
                    font-bold
                    leading-tight
                    tracking-tight
                    text-[#102F49]
                    sm:text-4xl
                  "
                >
                  {role === "business"
                    ? "Healthcare Professional Login"
                    : "Patient Login"}
                </motion.h2>
              </AnimatePresence>

              <p
                className="
                  mt-2
                  text-sm
                  text-slate-500
                  sm:text-base
                "
              >
                Welcome back to MRHealth
              </p>
            </motion.div>

            {/* ALERTS */}

            <div
              className="
                mt-4
                space-y-2.5
                sm:mt-6
                sm:space-y-3
              "
            >
              <AnimatePresence mode="popLayout">

                {successMessage && (
                  <motion.div
                    key="success"
                    variants={alertVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    layout
                    role="status"
                    aria-live="polite"
                    className="
                      flex
                      items-start
                      gap-3
                      rounded-xl
                      border
                      border-emerald-200
                      bg-emerald-50
                      p-3.5
                      text-sm
                      text-emerald-700
                    "
                  >
                    <FaCheckCircle
                      className="mt-0.5 shrink-0"
                      aria-hidden="true"
                    />

                    <span>
                      {successMessage}
                    </span>
                  </motion.div>
                )}

                {infoMessage && (
                  <motion.div
                    key="info"
                    variants={alertVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    layout
                    role="alert"
                    aria-live="assertive"
                    className="
                      flex
                      items-start
                      gap-3
                      rounded-xl
                      border
                      border-red-200
                      bg-red-50
                      p-3.5
                      text-sm
                      text-red-700
                    "
                  >
                    <FaExclamationCircle
                      className="mt-0.5 shrink-0"
                      aria-hidden="true"
                    />

                    <span>
                      {infoMessage}
                    </span>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* FORM */}

            <motion.form
              onSubmit={handleSubmit}
              variants={formContainerVariants}
              initial={
                shouldReduceMotion
                  ? false
                  : "hidden"
              }
              animate="visible"
              className="
                mt-5
                space-y-4
                sm:mt-8
                sm:space-y-5
              "
            >

              {/* =================================================
                  LOGIN AS
                  ================================================= */}

              <motion.div
                variants={formItemVariants}
              >
                <label
                  className="
                    mb-2
                    block
                    text-sm
                    font-semibold
                    text-[#263B4D]
                  "
                >
                  Login as
                </label>

                <div
                  className="
                    grid
                    grid-cols-2
                    gap-1
                    rounded-xl
                    border
                    border-slate-200
                    bg-slate-100
                    p-1
                  "
                >

                  {/* PATIENT */}

                  <motion.button
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      handleRoleChange("user")
                    }
                    whileHover={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 1.01,
                          }
                    }
                    whileTap={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 0.98,
                          }
                    }
                    className={`
                      flex
                      min-h-[46px]
                      items-center
                      justify-center
                      gap-2
                      rounded-lg
                      px-3
                      py-2
                      text-sm
                      font-semibold
                      transition-colors
                      duration-200
                      disabled:cursor-not-allowed
                      disabled:opacity-60

                      ${
                        role === "user"
                          ? `
                            bg-white
                            text-[#08A8E8]
                            shadow-sm
                          `
                          : `
                            text-slate-500
                            hover:text-[#102F49]
                          `
                      }
                    `}
                  >
                    <FaUserInjured className="shrink-0" />

                    <span>
                      Patient
                    </span>
                  </motion.button>

                  {/* =================================================
                      HEALTHCARE PROFESSIONAL
                      ================================================= */}

                  <motion.button
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      handleRoleChange("business")
                    }
                    whileHover={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 1.01,
                          }
                    }
                    whileTap={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 0.98,
                          }
                    }
                    className={`
                      flex
                      min-h-[46px]
                      items-center
                      justify-center
                      gap-2
                      rounded-lg
                      px-2
                      py-2
                      text-center
                      text-xs
                      font-semibold
                      leading-tight
                      transition-colors
                      duration-200
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                      sm:px-3
                      sm:text-sm

                      ${
                        role === "business"
                          ? `
                            bg-white
                            text-[#08A8E8]
                            shadow-sm
                          `
                          : `
                            text-slate-500
                            hover:text-[#102F49]
                          `
                      }
                    `}
                  >
                    <FaUserMd className="shrink-0" />

                    <span>
                      Healthcare Professional
                    </span>
                  </motion.button>

                </div>

                {/* PROFESSIONAL CATEGORY INFO */}

                {role === "business" && (
                  <motion.p
                    initial={
                      shouldReduceMotion
                        ? false
                        : {
                            opacity: 0,
                            y: -4,
                          }
                    }
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    className="
                      mt-2
                      text-center
                      text-[11px]
                      leading-5
                      text-slate-400
                      sm:text-xs
                    "
                  >
                    Doctor · Other Medical · Alternative Medical
                  </motion.p>
                )}
              </motion.div>

              {/* USERNAME */}

              <motion.div
                variants={formItemVariants}
              >
                <label
                  htmlFor="username"
                  className="
                    mb-2
                    block
                    text-sm
                    font-semibold
                    text-[#263B4D]
                  "
                >
                  Username or Email
                </label>

                <div className="relative">
                  <FaUser
                    className="
                      pointer-events-none
                      absolute
                      left-4
                      top-1/2
                      -translate-y-1/2
                      text-slate-400
                    "
                    aria-hidden="true"
                  />

                  <motion.input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);

                      dispatch(clearError());

                      setInfoMessage("");
                    }}
                    placeholder="Enter username or email"
                    autoComplete="username"
                    disabled={loading}
                    whileFocus={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 1.005,
                          }
                    }
                    className="
                      h-12
                      w-full
                      rounded-xl
                      border
                      border-slate-200
                      bg-white
                      pl-11
                      pr-4
                      text-sm
                      text-[#102F49]
                      outline-none
                      transition-all
                      placeholder:text-slate-400
                      hover:border-slate-300
                      focus:border-[#08A8E8]
                      focus:ring-4
                      focus:ring-[#08A8E8]/10
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                      sm:h-[52px]
                      sm:text-base
                    "
                  />
                </div>
              </motion.div>

              {/* PASSWORD */}

              <motion.div
                variants={formItemVariants}
              >
                <div
                  className="
                    mb-2
                    flex
                    items-center
                    justify-between
                    gap-3
                  "
                >
                  <label
                    htmlFor="password"
                    className="
                      text-sm
                      font-semibold
                      text-[#263B4D]
                    "
                  >
                    Password
                  </label>

                  <motion.button
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      navigate(
                        "/forgot-password"
                      )
                    }
                    whileHover={
                      shouldReduceMotion
                        ? {}
                        : {
                            x: 2,
                          }
                    }
                    className="
                      text-xs
                      font-semibold
                      text-[#079BD5]
                      transition
                      hover:text-[#087DAE]
                      hover:underline
                      disabled:opacity-50
                      sm:text-sm
                    "
                  >
                    Forgot Password?
                  </motion.button>
                </div>

                <div className="relative">
                  <FaLock
                    className="
                      pointer-events-none
                      absolute
                      left-4
                      top-1/2
                      -translate-y-1/2
                      text-slate-400
                    "
                    aria-hidden="true"
                  />

                  <motion.input
                    id="password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);

                      dispatch(clearError());

                      setInfoMessage("");
                    }}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={loading}
                    whileFocus={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 1.005,
                          }
                    }
                    className="
                      h-12
                      w-full
                      rounded-xl
                      border
                      border-slate-200
                      bg-white
                      pl-11
                      pr-12
                      text-sm
                      text-[#102F49]
                      outline-none
                      transition-all
                      placeholder:text-slate-400
                      hover:border-slate-300
                      focus:border-[#08A8E8]
                      focus:ring-4
                      focus:ring-[#08A8E8]/10
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                      sm:h-[52px]
                      sm:text-base
                    "
                  />

                  <motion.button
                    type="button"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    disabled={loading}
                    onClick={() =>
                      setShowPassword(
                        (prev) => !prev
                      )
                    }
                    whileHover={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 1.08,
                          }
                    }
                    whileTap={
                      shouldReduceMotion
                        ? {}
                        : {
                            scale: 0.92,
                          }
                    }
                    className="
                      absolute
                      right-2
                      top-1/2
                      flex
                      h-9
                      w-9
                      -translate-y-1/2
                      items-center
                      justify-center
                      rounded-lg
                      text-slate-400
                      transition
                      hover:bg-slate-100
                      hover:text-[#102F49]
                      disabled:opacity-50
                    "
                  >
                    {showPassword ? (
                      <FaEyeSlash />
                    ) : (
                      <FaEye />
                    )}
                  </motion.button>
                </div>
              </motion.div>

              {/* LOGIN BUTTON */}

              <motion.button
                type="submit"
                disabled={loading}
                variants={formItemVariants}
                whileHover={
                  loading ||
                  shouldReduceMotion
                    ? {}
                    : {
                        y: -2,
                        scale: 1.01,
                      }
                }
                whileTap={
                  loading ||
                  shouldReduceMotion
                    ? {}
                    : {
                        scale: 0.98,
                      }
                }
                className={`
                  group
                  flex
                  min-h-[52px]
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  px-5
                  text-sm
                  font-bold
                  text-white
                  transition-colors
                  duration-200
                  sm:text-base

                  ${
                    loading
                      ? `
                        cursor-not-allowed
                        bg-slate-400
                      `
                      : `
                        bg-[#08A8E8]
                        shadow-lg
                        shadow-[#08A8E8]/20
                        hover:bg-[#079BD5]
                        hover:shadow-xl
                        hover:shadow-[#08A8E8]/25
                      `
                  }
                `}
              >
                {loading ? (
                  <>
                    <motion.span
                      animate={
                        shouldReduceMotion
                          ? {}
                          : {
                              rotate: 360,
                            }
                      }
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="
                        h-5
                        w-5
                        rounded-full
                        border-2
                        border-white/30
                        border-t-white
                      "
                      aria-hidden="true"
                    />

                    <span>
                      Logging in...
                    </span>
                  </>
                ) : (
                  <>
                    <span>
                      Login to MRHealth
                    </span>

                    <motion.span
                      whileHover={
                        shouldReduceMotion
                          ? {}
                          : {
                              x: 5,
                            }
                      }
                    >
                      <FaArrowRight />
                    </motion.span>
                  </>
                )}
              </motion.button>

              {/* DIVIDER */}

              <motion.div
                variants={formItemVariants}
                className="
                  relative
                  py-1
                  sm:py-2
                "
              >
                <div
                  className="
                    absolute
                    inset-0
                    flex
                    items-center
                  "
                >
                  <div
                    className="
                      w-full
                      border-t
                      border-slate-200
                    "
                  />
                </div>

                <div
                  className="
                    relative
                    flex
                    justify-center
                  "
                >
                  <span
                    className="
                      bg-[#F7FAFC]
                      px-3
                      text-xs
                      text-slate-400
                    "
                  >
                    New to MRHealth?
                  </span>
                </div>
              </motion.div>

              {/* PATIENT ACCOUNT */}

              <motion.button
                type="button"
                disabled={loading}
                onClick={() =>
                  navigate("/sign-up")
                }
                variants={formItemVariants}
                whileHover={
                  loading ||
                  shouldReduceMotion
                    ? {}
                    : {
                        y: -2,
                        scale: 1.01,
                      }
                }
                whileTap={
                  loading ||
                  shouldReduceMotion
                    ? {}
                    : {
                        scale: 0.98,
                      }
                }
                className="
                  flex
                  min-h-[46px]
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  border
                  border-[#B8E8F6]
                  bg-[#EFFAFF]
                  px-4
                  text-sm
                  font-bold
                  text-[#078EBF]
                  transition-colors
                  hover:border-[#8DDCF1]
                  hover:bg-[#E3F8FD]
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                  sm:min-h-[48px]
                  sm:text-base
                "
              >
                <FaUserPlus />

                <span>
                  Create Patient Account
                </span>
              </motion.button>

              {/* HEALTHCARE PROFESSIONAL REGISTRATION */}

              <motion.button
                type="button"
                disabled={loading}
                onClick={() =>
                  navigate(
                    "/RegistrationForm"
                  )
                }
                variants={formItemVariants}
                whileHover={
                  loading ||
                  shouldReduceMotion
                    ? {}
                    : {
                        y: -2,
                        scale: 1.01,
                      }
                }
                whileTap={
                  loading ||
                  shouldReduceMotion
                    ? {}
                    : {
                        scale: 0.98,
                      }
                }
                className="
                  flex
                  min-h-[46px]
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  border
                  border-[#C8D9E5]
                  bg-white
                  px-4
                  text-sm
                  font-bold
                  text-[#24465E]
                  transition-colors
                  hover:border-[#9FB8C8]
                  hover:bg-[#F5F9FB]
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                  sm:min-h-[48px]
                  sm:text-base
                "
              >
                <FaStethoscope />

                <span>
                  Register as Healthcare Professional
                </span>
              </motion.button>

            </motion.form>

            {/* SECURITY */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? false
                  : {
                      opacity: 0,
                      y: 10,
                    }
              }
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.7,
                delay: 1.1,
              }}
              className="
                mt-5
                flex
                items-center
                justify-center
                gap-2
                text-center
                text-[11px]
                text-slate-400
                sm:mt-6
                sm:text-xs
              "
            >
              <motion.div
                animate={
                  shouldReduceMotion
                    ? {}
                    : {
                        scale: [
                          1,
                          1.12,
                          1,
                        ],
                      }
                }
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <FaShieldAlt
                  className="
                    shrink-0
                    text-emerald-500
                  "
                />
              </motion.div>

              <span>
                Your login information is
                securely protected.
              </span>
            </motion.div>

            {/* MOBILE FOOTER */}

            <motion.div
              initial={
                shouldReduceMotion
                  ? false
                  : {
                      opacity: 0,
                    }
              }
              animate={{
                opacity: 1,
              }}
              transition={{
                duration: 0.8,
                delay: 1.2,
              }}
              className="
                mt-4
                text-center
                text-[11px]
                text-slate-400
                sm:mt-5
                lg:hidden
              "
            >
              ©{" "}
              {new Date().getFullYear()}{" "}
              MRHealth
              {" · "}mrhealth.co.in
            </motion.div>

          </div>
        </motion.section>
      </div>
    </main>
  );
};

export default Login;


// import React, {
//   useEffect,
//   useRef,
//   useState,
// } from "react";

// import { useDispatch, useSelector } from "react-redux";

// import {
//   useNavigate,
//   useLocation,
//   Link,
// } from "react-router-dom";

// import {
//   loginUser,
//   clearError,
// } from "../../redux/slices/authSlice";

// import {
//   FaUser,
//   FaLock,
//   FaEye,
//   FaEyeSlash,
//   FaUserMd,
//   FaUserInjured,
//   FaArrowRight,
//   FaShieldAlt,
//   FaCheckCircle,
//   FaExclamationCircle,
//   FaInfoCircle,
//   FaUserPlus,
//   FaStethoscope,
// } from "react-icons/fa";

// import {
//   motion,
//   AnimatePresence,
//   useReducedMotion,
// } from "motion/react";

// import logo from "../../Pages/Header/logo.png";

// /* =============================================================
//    MRHEALTH BRAND GRADIENT
//    ============================================================= */

// const MRHEALTH_GRADIENT =
//   "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)";

// /* =============================================================
//    MOTION VARIANTS
//    ============================================================= */

// const leftPanelVariants = {
//   hidden: {
//     opacity: 0,
//     x: -35,
//   },

//   visible: {
//     opacity: 1,
//     x: 0,
//     transition: {
//       duration: 1.2,
//       ease: [0.22, 1, 0.36, 1],
//     },
//   },
// };

// const rightPanelVariants = {
//   hidden: {
//     opacity: 0,
//     x: 35,
//   },

//   visible: {
//     opacity: 1,
//     x: 0,
//     transition: {
//       duration: 1.2,
//       delay: 0.15,
//       ease: [0.22, 1, 0.36, 1],
//     },
//   },
// };

// const heroContainerVariants = {
//   hidden: {},

//   visible: {
//     transition: {
//       delayChildren: 0.35,
//       staggerChildren: 0.18,
//     },
//   },
// };

// const heroItemVariants = {
//   hidden: {
//     opacity: 0,
//     y: 18,
//   },

//   visible: {
//     opacity: 1,
//     y: 0,
//     transition: {
//       duration: 0.9,
//       ease: [0.22, 1, 0.36, 1],
//     },
//   },
// };

// const formContainerVariants = {
//   hidden: {},

//   visible: {
//     transition: {
//       delayChildren: 0.3,
//       staggerChildren: 0.12,
//     },
//   },
// };

// const formItemVariants = {
//   hidden: {
//     opacity: 0,
//     y: 12,
//   },

//   visible: {
//     opacity: 1,
//     y: 0,
//     transition: {
//       duration: 0.7,
//       ease: [0.22, 1, 0.36, 1],
//     },
//   },
// };

// const alertVariants = {
//   hidden: {
//     opacity: 0,
//     y: -10,
//     scale: 0.98,
//   },

//   visible: {
//     opacity: 1,
//     y: 0,
//     scale: 1,
//     transition: {
//       duration: 0.4,
//       ease: [0.22, 1, 0.36, 1],
//     },
//   },

//   exit: {
//     opacity: 0,
//     y: -8,
//     scale: 0.98,
//     transition: {
//       duration: 0.3,
//     },
//   },
// };

// const roleIconVariants = {
//   initial: {
//     opacity: 0,
//     scale: 0.5,
//     rotate: -15,
//   },

//   animate: {
//     opacity: 1,
//     scale: 1,
//     rotate: 0,
//     transition: {
//       duration: 0.5,
//       ease: [0.22, 1, 0.36, 1],
//     },
//   },

//   exit: {
//     opacity: 0,
//     scale: 0.5,
//     rotate: 15,
//     transition: {
//       duration: 0.3,
//     },
//   },
// };

// /* =============================================================
//    COMPONENT
//    ============================================================= */

// const Login = () => {
//   /* ===========================================================
//      STATE
//      =========================================================== */

//   const [username, setUsername] =
//     useState("");

//   const [password, setPassword] =
//     useState("");

//   // user = patient
//   // business = doctor / healthcare professional
//   const [role, setRole] =
//     useState("user");

//   const [showPassword, setShowPassword] =
//     useState(false);

//   const [successMessage, setSuccessMessage] =
//     useState("");

//   const [infoMessage, setInfoMessage] =
//     useState("");

//   /* ===========================================================
//      REDIRECT TIMER REFS
//      =========================================================== */

//   const redirectTimerRef =
//     useRef(null);

//   const successTimerRef =
//     useRef(null);

//   /* ===========================================================
//      REDUX / ROUTER
//      =========================================================== */

//   const dispatch = useDispatch();

//   const navigate = useNavigate();

//   const location = useLocation();

//   const { loading } = useSelector(
//     (state) => state.auth
//   );

//   /* ===========================================================
//      REDUCED MOTION
//      =========================================================== */

//   const shouldReduceMotion =
//     useReducedMotion();

//   /* ===========================================================
//      CLEANUP TIMERS
//      =========================================================== */

//   useEffect(() => {
//     return () => {
//       if (redirectTimerRef.current) {
//         clearTimeout(
//           redirectTimerRef.current
//         );
//       }

//       if (successTimerRef.current) {
//         clearTimeout(
//           successTimerRef.current
//         );
//       }
//     };
//   }, []);

//   /* ===========================================================
//      SUCCESS MESSAGE FROM SIGNUP
//      =========================================================== */

//   useEffect(() => {
//     const success =
//       location.state?.success;

//     if (!success) {
//       return;
//     }

//     setSuccessMessage(success);

//     /*
//      * Clear any login info message when
//      * a signup success message arrives.
//      */
//     setInfoMessage("");

//     /*
//      * Clear Redux auth error as well.
//      */
//     dispatch(clearError());

//     navigate(location.pathname, {
//       replace: true,
//       state: {},
//     });

//     if (successTimerRef.current) {
//       clearTimeout(
//         successTimerRef.current
//       );
//     }

//     successTimerRef.current =
//       setTimeout(() => {
//         setSuccessMessage("");
//       }, 5000);

//     return () => {
//       if (successTimerRef.current) {
//         clearTimeout(
//           successTimerRef.current
//         );
//       }
//     };
//   }, [
//     location.state,
//     location.pathname,
//     navigate,
//     dispatch,
//   ]);

//   /* ===========================================================
//      ROLE CHANGE
//      =========================================================== */

//   const handleRoleChange = (
//     newRole
//   ) => {
//     if (loading) {
//       return;
//     }

//     setRole(newRole);

//     setInfoMessage("");

//     setSuccessMessage("");

//     dispatch(clearError());
//   };

//   /* ===========================================================
//      GET AVAILABILITY PAGE
//      =========================================================== */

//   const getAvailabilityPath = (
//     categoryPath
//   ) => {
//     const primaryCategory =
//       String(
//         categoryPath?.[0] ?? ""
//       )
//         .toLowerCase()
//         .trim();

//     if (
//       primaryCategory.includes(
//         "alternative"
//       )
//     ) {
//       return "/availabilities/alternative";
//     }

//     if (
//       primaryCategory.includes(
//         "doctor"
//       )
//     ) {
//       return "/availabilities/doctor";
//     }

//     if (
//       primaryCategory.includes(
//         "other medical"
//       )
//     ) {
//       return "/availabilities/other-medical";
//     }

//     return "/availabilities";
//   };

//   /* ===========================================================
//      GO TO AVAILABILITY
//      =========================================================== */

//   const goToAvailability = (
//     categoryPath
//   ) => {
//     const path =
//       getAvailabilityPath(
//         categoryPath
//       );

//     navigate(path, {
//       replace: true,
//     });
//   };

//   /* ===========================================================
//      SAFE REDIRECT
//      =========================================================== */

//   const scheduleRedirect = (
//     callback,
//     delay
//   ) => {
//     if (redirectTimerRef.current) {
//       clearTimeout(
//         redirectTimerRef.current
//       );
//     }

//     redirectTimerRef.current =
//       setTimeout(() => {
//         redirectTimerRef.current =
//           null;

//         callback();
//       }, delay);
//   };

//   /* ===========================================================
//      HANDLE LOGIN
//      =========================================================== */

//   const handleSubmit = async (
//     e
//   ) => {
//     e.preventDefault();

//     /* ---------------------------------------------------------
//        PREVENT DUPLICATE LOGIN REQUESTS
//        --------------------------------------------------------- */

//     if (loading) {
//       return;
//     }

//     /* ---------------------------------------------------------
//        CLEAR PREVIOUS MESSAGES
//        --------------------------------------------------------- */

//     setInfoMessage("");

//     setSuccessMessage("");

//     dispatch(clearError());

//     /* =========================================================
//        VALIDATION
//        ========================================================= */

//     const trimmedUsername =
//       username.trim();

//     if (!trimmedUsername) {
//       setInfoMessage(
//         "Please enter your username or email."
//       );

//       return;
//     }

//     if (!password) {
//       setInfoMessage(
//         "Please enter your password."
//       );

//       return;
//     }

//     /* =========================================================
//        LOGIN REQUEST
//        ========================================================= */

//     try {
//       const resultAction =
//         await dispatch(
//           loginUser({
//             username:
//               trimmedUsername,
//             password,
//             role,
//           })
//         );

//       /* =======================================================
//          LOGIN REJECTED
//          ======================================================= */

//       if (
//         loginUser.rejected.match(
//           resultAction
//         )
//       ) {
//         const rejectedPayload =
//           resultAction.payload ?? {};

//         const rejectedError =
//           resultAction.error ?? {};

//         /* -----------------------------------------------------
//            REDIRECT PATH
//            ----------------------------------------------------- */

//         const redirectPath =
//           rejectedPayload?.redirect ??
//           rejectedPayload?.response
//             ?.data?.redirect ??
//           rejectedError?.redirect ??
//           rejectedError?.response
//             ?.data?.redirect ??
//           null;

//         /* -----------------------------------------------------
//            BACKEND MESSAGE
//            ----------------------------------------------------- */

//         const backendMessage =
//           rejectedPayload?.message ??
//           rejectedPayload?.response
//             ?.data?.message ??
//           rejectedPayload?.error ??
//           rejectedError?.message ??
//           "";

//         /* -----------------------------------------------------
//            REDIRECT REQUESTED BY BACKEND
//            ----------------------------------------------------- */

//         if (redirectPath) {
//           setInfoMessage(
//             backendMessage ||
//               "Redirecting..."
//           );

//           scheduleRedirect(
//             () => {
//               navigate(
//                 redirectPath,
//                 {
//                   replace: true,
//                 }
//               );
//             },
//             500
//           );

//           return;
//         }

//         /* -----------------------------------------------------
//            NORMAL LOGIN ERROR

//            IMPORTANT:
//            We display the error ONLY through infoMessage.
//            We do NOT render Redux error separately.
//            This prevents duplicate "User not found".
//            ----------------------------------------------------- */

//         setInfoMessage(
//           backendMessage ||
//             "Login failed. Please check your credentials and try again."
//         );

//         return;
//       }

//       /* =======================================================
//          MAKE SURE LOGIN REALLY SUCCEEDED
//          ======================================================= */

//       if (
//         !loginUser.fulfilled.match(
//           resultAction
//         )
//       ) {
//         setInfoMessage(
//           "Login could not be completed. Please try again."
//         );

//         return;
//       }

//       /* =======================================================
//          LOGIN SUCCESS
//          ======================================================= */

//       const payload =
//         resultAction.payload ?? {};

//       /* -------------------------------------------------------
//          VERIFY USER
//          ------------------------------------------------------- */

//       if (!payload?.user) {
//         setInfoMessage(
//           "Login succeeded, but account details could not be loaded. Please try again."
//         );

//         return;
//       }

//       const loggedInUser =
//         payload.user;

//       /* =======================================================
//          USER ROLE
//          ======================================================= */

//       const userRole =
//         String(
//           loggedInUser?.role ?? ""
//         )
//           .toLowerCase()
//           .trim();

//       /* =======================================================
//          CATEGORY PATH
//          ======================================================= */

//       const categoryPath =
//         Array.isArray(
//           loggedInUser?.categoryPath
//         )
//           ? loggedInUser.categoryPath
//           : [];

//       /* =======================================================
//          PRIMARY CATEGORY
//          ======================================================= */

//       const primaryCategory =
//         String(
//           categoryPath?.[0] ?? ""
//         )
//           .toLowerCase()
//           .trim();

//       /* =======================================================
//          AVAILABILITY
//          ======================================================= */

//       const hasAvailabilitySetup =
//         payload?.hasAvailabilitySetup ===
//         true;

//       const weekStatus =
//         payload?.weekStatus ?? null;

//       const weeklyAvailability =
//         Array.isArray(
//           payload?.weeklyAvailability
//         )
//           ? payload.weeklyAvailability
//           : [];

//       /* =======================================================
//          RESPONSE DATA
//          ======================================================= */

//       const code =
//         payload?.code ?? null;

//       const message =
//         payload?.message ?? "";

//       /* =======================================================
//          UPCOMING AVAILABILITY
//          ======================================================= */

//       const hasUpcomingSlots =
//         weeklyAvailability.length > 0;

//       /* =======================================================
//          SUBSCRIPTION EXPIRED
//          ======================================================= */

//       if (
//         code ===
//           "SUBSCRIPTION_EXPIRED" ||
//         payload?.subscriptionExpired ===
//           true
//       ) {
//         setInfoMessage(
//           message ||
//             "Your subscription has expired. Redirecting to plans..."
//         );

//         scheduleRedirect(
//           () => {
//             navigate(
//               "/select-plan",
//               {
//                 replace: true,
//               }
//             );
//           },
//           1500
//         );

//         return;
//       }

//       /* =======================================================
//          USER / PATIENT
//          ======================================================= */

//       if (
//         userRole === "user"
//       ) {
//         navigate(
//           "/dashboard/user",
//           {
//             replace: true,
//           }
//         );

//         return;
//       }

//       /* =======================================================
//          BUSINESS / DOCTOR
//          ======================================================= */

//       if (
//         userRole === "business" ||
//         userRole === "doctor"
//       ) {
//         /* -----------------------------------------------------
//            CHECK AVAILABILITY
//            ----------------------------------------------------- */

//         const needsAvailabilityUpdate =
//           !hasAvailabilitySetup ||
//           weekStatus === "PAST_DATA";

//         /* -----------------------------------------------------
//            AVAILABILITY NOT SET / EXPIRED
//            ----------------------------------------------------- */

//         if (
//           needsAvailabilityUpdate
//         ) {
//           setInfoMessage(
//             !hasAvailabilitySetup
//               ? "Please set your availability before continuing."
//               : "Your previous availability has expired. Please update your availability."
//           );

//           scheduleRedirect(
//             () => {
//               goToAvailability(
//                 categoryPath
//               );
//             },
//             700
//           );

//           return;
//         }

//         /* -----------------------------------------------------
//            NO UPCOMING SLOTS
//            ----------------------------------------------------- */

//         if (!hasUpcomingSlots) {
//           setInfoMessage(
//             "No slots available for this week. Please update your availability."
//           );

//           scheduleRedirect(
//             () => {
//               goToAvailability(
//                 categoryPath
//               );
//             },
//             1200
//           );

//           return;
//         }

//         /* -----------------------------------------------------
//            OTHER MEDICAL SERVICE
//            ----------------------------------------------------- */

//         if (
//           primaryCategory.includes(
//             "other medical"
//           )
//         ) {
//           navigate(
//             "/dashboard/medical-service",
//             {
//               replace: true,
//             }
//           );

//           return;
//         }

//         /* -----------------------------------------------------
//            BUSINESS / DOCTOR DASHBOARD
//            ----------------------------------------------------- */

//         navigate(
//           "/dashboard/business-user",
//           {
//             replace: true,
//           }
//         );

//         return;
//       }

//       /* =======================================================
//          UNKNOWN ROLE
//          ======================================================= */

//       setInfoMessage(
//         "Unable to determine your account type. Please contact support."
//       );
//     } catch (err) {
//       /* =======================================================
//          UNEXPECTED ERROR
//          ======================================================= */

//       console.error(
//         "Login unexpected error:",
//         err
//       );

//       setInfoMessage(
//         "Something went wrong. Please try again."
//       );
//     }
//   };

//   /* =============================================================
//      UI
//      ============================================================= */

//   return (
//     <main
//       className="
//         min-h-[100dvh]
//         w-full
//         overflow-x-hidden
//         bg-[#F7FAFC]
//       "
//     >
//       <div
//         className="
//           min-h-[100dvh]
//           w-full
//           lg:flex
//           lg:items-stretch
//         "
//       >

//         {/* =====================================================
//             LEFT BRANDING PANEL
//             ===================================================== */}

//         <motion.section
//           initial={
//             shouldReduceMotion
//               ? false
//               : "hidden"
//           }
//           animate="visible"
//           variants={
//             leftPanelVariants
//           }
//           className="
//             relative
//             hidden
//             min-h-[100dvh]
//             overflow-hidden
//             lg:flex
//             lg:w-[48%]
//             lg:flex-col
//             lg:justify-between
//             lg:px-10
//             lg:py-8
//             xl:w-[50%]
//             xl:px-14
//             2xl:px-20
//           "
//           style={{
//             background: `
//               linear-gradient(
//                 135deg,
//                 #071D30 0%,
//                 #0B2942 38%,
//                 #103B59 68%,
//                 #0A2A43 100%
//               )
//             `,
//           }}
//         >

//           {/* =================================================
//               TOP LEFT HALF CIRCLE
//               ================================================= */}

//           <motion.div
//             initial={
//               shouldReduceMotion
//                 ? false
//                 : {
//                     opacity: 0,
//                     scale: 0.85,
//                   }
//             }
//             animate={{
//               opacity: 1,
//               scale: 1,
//             }}
//             transition={{
//               duration: 1.2,
//               delay: 0.25,
//               ease: [
//                 0.22,
//                 1,
//                 0.36,
//                 1,
//               ],
//             }}
//             className="
//               pointer-events-none
//               absolute
//               -left-[230px]
//               -top-[230px]
//               h-[460px]
//               w-[460px]
//               rounded-full
//               border-[1px]
//               border-[#4FD1FF]/20
//               bg-[#08A8E8]/[0.08]
//             "
//           />

//           {/* =================================================
//               TOP LEFT INNER HALF CIRCLE
//               ================================================= */}

//           <motion.div
//             initial={
//               shouldReduceMotion
//                 ? false
//                 : {
//                     opacity: 0,
//                     scale: 0.8,
//                   }
//             }
//             animate={{
//               opacity: 1,
//               scale: 1,
//             }}
//             transition={{
//               duration: 1.4,
//               delay: 0.4,
//               ease: [
//                 0.22,
//                 1,
//                 0.36,
//                 1,
//               ],
//             }}
//             className="
//               pointer-events-none
//               absolute
//               -left-[145px]
//               -top-[145px]
//               h-[290px]
//               w-[290px]
//               rounded-full
//               border-[1px]
//               border-[#6DDAFF]/15
//               bg-[#08A8E8]/[0.05]
//             "
//           />

//           {/* =================================================
//               BOTTOM RIGHT HALF CIRCLE
//               ================================================= */}

//           <motion.div
//             initial={
//               shouldReduceMotion
//                 ? false
//                 : {
//                     opacity: 0,
//                     scale: 0.85,
//                   }
//             }
//             animate={{
//               opacity: 1,
//               scale: 1,
//             }}
//             transition={{
//               duration: 1.3,
//               delay: 0.35,
//               ease: [
//                 0.22,
//                 1,
//                 0.36,
//                 1,
//               ],
//             }}
//             className="
//               pointer-events-none
//               absolute
//               -bottom-[260px]
//               -right-[260px]
//               h-[520px]
//               w-[520px]
//               rounded-full
//               border-[1px]
//               border-[#4FD1FF]/20
//               bg-[#08A8E8]/[0.07]
//             "
//           />

//           {/* =================================================
//               BOTTOM RIGHT INNER HALF CIRCLE
//               ================================================= */}

//           <motion.div
//             initial={
//               shouldReduceMotion
//                 ? false
//                 : {
//                     opacity: 0,
//                     scale: 0.8,
//                   }
//             }
//             animate={{
//               opacity: 1,
//               scale: 1,
//             }}
//             transition={{
//               duration: 1.5,
//               delay: 0.5,
//               ease: [
//                 0.22,
//                 1,
//                 0.36,
//                 1,
//               ],
//             }}
//             className="
//               pointer-events-none
//               absolute
//               -bottom-[165px]
//               -right-[165px]
//               h-[330px]
//               w-[330px]
//               rounded-full
//               border-[1px]
//               border-[#6DDAFF]/15
//               bg-[#08A8E8]/[0.04]
//             "
//           />

//           {/* =================================================
//               VERY SUBTLE CENTER LINE
//               ================================================= */}

//           <div
//             className="
//               pointer-events-none
//               absolute
//               inset-y-0
//               right-0
//               w-px
//               bg-gradient-to-b
//               from-transparent
//               via-[#4FD1FF]/25
//               to-transparent
//             "
//           />

//           {/* =================================================
//               BRAND CONTENT
//               ================================================= */}

//           <div
//             className="
//               relative
//               z-10
//               mx-auto
//               flex
//               min-h-[430px]
//               w-full
//               max-w-xl
//               flex-col
//               lg:h-full
//               lg:min-h-0
//             "
//           >

//             {/* =================================================
//                 LOGO
//                 ================================================= */}

//             <button
//               type="button"
//               onClick={() =>
//                 navigate("/Home")
//               }
//               className="
//                 flex
//                 shrink-0
//                 items-center
//               "
//             >
//               <img
//                 src="/logo.jpeg"
//                 alt="MR Health"
//                 title="MR Health"
//                 className="
//                   h-12
//                   w-auto
//                   object-contain
//                   sm:h-14
//                   md:h-16
//                 "
//               />
//             </button>

//             {/* =================================================
//                 HERO CONTENT
//                 ================================================= */}

//             <motion.div
//               variants={
//                 heroContainerVariants
//               }
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : "hidden"
//               }
//               animate="visible"
//               className="
//                 mt-auto
//                 pb-8
//                 pt-16
//                 sm:pt-20
//                 lg:pb-12
//                 lg:pt-20
//                 xl:pb-14
//                 xl:pt-24
//               "
//             >

//               {/* EYEBROW */}

//               <motion.div
//                 variants={
//                   heroItemVariants
//                 }
//                 className="
//                   mb-5
//                   inline-flex
//                   items-center
//                   gap-2
//                   rounded-full
//                   border
//                   border-[#4FD1FF]/25
//                   bg-[#08A8E8]/10
//                   px-3
//                   py-1.5
//                   text-[10px]
//                   font-semibold
//                   uppercase
//                   tracking-[0.18em]
//                   text-[#6DDAFF]
//                   shadow-lg
//                   shadow-[#08A8E8]/5
//                   backdrop-blur-md
//                   sm:text-xs
//                   sm:tracking-[0.2em]
//                 "
//               >
//                 <span
//                   className="
//                     h-1.5
//                     w-1.5
//                     rounded-full
//                     bg-[#4FD1FF]
//                     shadow-[0_0_10px_rgba(79,209,255,0.8)]
//                   "
//                 />

//                 Healthcare scheduling
//                 platform
//               </motion.div>

//               {/* HEADING */}

//               <motion.h1
//                 variants={
//                   heroItemVariants
//                 }
//                 className="
//                   max-w-[600px]
//                   font-serif
//                   text-[34px]
//                   font-bold
//                   leading-[1.12]
//                   tracking-[-0.02em]
//                   text-white
//                   sm:text-[40px]
//                   md:text-[46px]
//                   lg:text-[42px]
//                   xl:text-[52px]
//                   2xl:text-[58px]
//                 "
//               >
//                 Connect with the
//                 <br />
//                 right doctor at
//                 <br />

//                 <span
//                   className="
//                     bg-gradient-to-r
//                     from-[#4FD1FF]
//                     via-[#7DE3FF]
//                     to-[#38BDF8]
//                     bg-clip-text
//                     text-transparent
//                   "
//                 >
//                   the right time.
//                 </span>
//               </motion.h1>

//               {/* DESCRIPTION */}

//               <motion.p
//                 variants={
//                   heroItemVariants
//                 }
//                 className="
//                   mt-6
//                   max-w-[520px]
//                   text-sm
//                   leading-6
//                   text-[#B5CBDC]
//                   sm:mt-7
//                   sm:text-base
//                   sm:leading-7
//                   md:max-w-[540px]
//                   md:text-lg
//                   md:leading-8
//                 "
//               >
//                 Streamlined appointment
//                 booking for patients and
//                 comprehensive schedule
//                 management for healthcare
//                 providers.
//               </motion.p>

//               {/* TRUST INDICATORS */}

//               <motion.div
//                 variants={
//                   heroItemVariants
//                 }
//                 className="
//                   mt-8
//                   flex
//                   flex-wrap
//                   items-center
//                   gap-x-6
//                   gap-y-3
//                   text-xs
//                   text-[#9CB8CC]
//                   sm:text-sm
//                 "
//               >
//                 <div className="flex items-center gap-2">
//                   <FaCheckCircle className="text-[#4FD1FF]" />
//                   Secure platform
//                 </div>

//                 <div className="flex items-center gap-2">
//                   <FaCheckCircle className="text-[#4FD1FF]" />
//                   Easy scheduling
//                 </div>

//                 <div className="flex items-center gap-2">
//                   <FaCheckCircle className="text-[#4FD1FF]" />
//                   Healthcare focused
//                 </div>
//               </motion.div>
//             </motion.div>

//             {/* BRAND FOOTER */}

//             <motion.div
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : {
//                       opacity: 0,
//                     }
//               }
//               animate={{
//                 opacity: 1,
//               }}
//               transition={{
//                 duration: 0.8,
//                 delay: 1.2,
//               }}
//               className="
//                 hidden
//                 text-xs
//                 text-[#7899B0]
//                 lg:block
//               "
//             >
//               ©{" "}
//               {new Date().getFullYear()}{" "}
//               MRHealth
//               {" · "}mrhealth.co.in
//             </motion.div>
//           </div>
//         </motion.section>

//         {/* =====================================================
//             RIGHT LOGIN PANEL
//             ===================================================== */}

//         <motion.section
//           initial={
//             shouldReduceMotion
//               ? false
//               : "hidden"
//           }
//           animate="visible"
//           variants={
//             rightPanelVariants
//           }
//           className="
//             relative
//             flex
//             min-h-[100dvh]
//             w-full
//             items-start
//             justify-center
//             bg-[#F7FAFC]
//             px-4
//             py-6
//             sm:px-6
//             sm:py-8
//             md:px-10
//             md:py-10
//             lg:min-h-[100dvh]
//             lg:w-[52%]
//             lg:items-center
//             lg:px-10
//             xl:w-[50%]
//             xl:px-16
//             2xl:px-24
//           "
//         >

//           {/* =================================================
//               SUBTLE RIGHT CORNER DESIGN
//               ================================================= */}

//           <div
//             className="
//               pointer-events-none
//               absolute
//               -right-32
//               -top-32
//               hidden
//               h-64
//               w-64
//               rounded-full
//               border
//               border-[#08A8E8]/10
//               bg-[#08A8E8]/[0.025]
//               lg:block
//             "
//           />

//           <div
//             className="
//               pointer-events-none
//               absolute
//               -bottom-40
//               -left-40
//               hidden
//               h-72
//               w-72
//               rounded-full
//               border
//               border-[#08A8E8]/10
//               bg-[#08A8E8]/[0.02]
//               lg:block
//             "
//           />

//           <div
//             className="
//               relative
//               z-10
//               w-full
//               max-w-[460px]
//             "
//           >

//             {/* =================================================
//                 MOBILE BRAND
//                 ================================================= */}

//             <motion.div
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : {
//                       opacity: 0,
//                       y: -15,
//                     }
//               }
//               animate={{
//                 opacity: 1,
//                 y: 0,
//               }}
//               transition={{
//                 duration: 0.8,
//                 delay: 0.25,
//               }}
//               className="
//                 mb-5
//                 flex
//                 justify-center
//                 sm:mb-7
//                 lg:hidden
//               "
//             >
//               <Link
//                 to="/"
//                 className="
//                   inline-flex
//                   items-center
//                   gap-3
//                 "
//               >
//                 <motion.div
//                   whileTap={
//                     shouldReduceMotion
//                       ? {}
//                       : {
//                           scale: 0.94,
//                         }
//                   }
//                   className="
//                     relative
//                     flex
//                     h-10
//                     w-10
//                     items-center
//                     justify-center
//                     overflow-hidden
//                     rounded-full
//                     border
//                     border-white/15
//                     shadow-lg
//                     shadow-black/20
//                   "
//                   style={{
//                     background:
//                       MRHEALTH_GRADIENT,
//                   }}
//                 >
//                   <div
//                     className="
//                       pointer-events-none
//                       absolute
//                       inset-0
//                       rounded-full
//                       bg-white/5
//                     "
//                   />

//                   <img
//                     src={logo}
//                     alt="MRHealth"
//                     className="
//                       relative
//                       z-10
//                       h-full
//                       w-full
//                       object-contain
//                       p-1.5
//                       brightness-0
//                       invert
//                     "
//                   />
//                 </motion.div>

//                 <span
//                   className="
//                     text-lg
//                     font-bold
//                     text-[#102F49]
//                   "
//                 >
//                   MRHealth
//                 </span>
//               </Link>
//             </motion.div>

//             {/* =================================================
//                 LOGIN HEADER
//                 ================================================= */}

//             <motion.div
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : {
//                       opacity: 0,
//                       y: 20,
//                     }
//               }
//               animate={{
//                 opacity: 1,
//                 y: 0,
//               }}
//               transition={{
//                 duration: 0.8,
//                 delay: 0.35,
//               }}
//               className="text-center"
//             >
//               <motion.div
//                 whileHover={
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         scale: 1.05,
//                         rotate: 2,
//                       }
//                 }
//                 className="
//                   mx-auto
//                   mb-3
//                   flex
//                   h-12
//                   w-12
//                   items-center
//                   justify-center
//                   rounded-2xl
//                   bg-[#E6F7FC]
//                   text-[#08A8E8]
//                   shadow-sm
//                   sm:mb-5
//                   sm:h-16
//                   sm:w-16
//                 "
//               >
//                 <AnimatePresence
//                   mode="wait"
//                   initial={false}
//                 >
//                   <motion.div
//                     key={role}
//                     variants={
//                       roleIconVariants
//                     }
//                     initial="initial"
//                     animate="animate"
//                     exit="exit"
//                   >
//                     {role ===
//                     "business" ? (
//                       <FaUserMd className="text-2xl sm:text-3xl" />
//                     ) : (
//                       <FaUserInjured className="text-2xl sm:text-3xl" />
//                     )}
//                   </motion.div>
//                 </AnimatePresence>
//               </motion.div>

//               <AnimatePresence
//                 mode="wait"
//                 initial={false}
//               >
//                 <motion.h2
//                   key={role}
//                   initial={
//                     shouldReduceMotion
//                       ? false
//                       : {
//                           opacity: 0,
//                           y: 8,
//                         }
//                   }
//                   animate={{
//                     opacity: 1,
//                     y: 0,
//                   }}
//                   exit={
//                     shouldReduceMotion
//                       ? {}
//                       : {
//                           opacity: 0,
//                           y: -8,
//                         }
//                   }
//                   transition={{
//                     duration: 0.4,
//                   }}
//                   className="
//                     font-serif
//                     text-2xl
//                     font-bold
//                     leading-tight
//                     tracking-tight
//                     text-[#102F49]
//                     sm:text-4xl
//                   "
//                 >
//                   {role ===
//                   "business"
//                     ? "Doctor Login"
//                     : "Patient Login"}
//                 </motion.h2>
//               </AnimatePresence>

//               <p
//                 className="
//                   mt-2
//                   text-sm
//                   text-slate-500
//                   sm:text-base
//                 "
//               >
//                 Welcome back to MRHealth
//               </p>
//             </motion.div>

//             {/* =================================================
//                 ALERTS
//                 ================================================= */}

//             <div
//               className="
//                 mt-4
//                 space-y-2.5
//                 sm:mt-6
//                 sm:space-y-3
//               "
//             >
//               <AnimatePresence
//                 mode="popLayout"
//               >

//                 {/* =================================================
//                     SUCCESS
//                     ================================================= */}

//                 {successMessage && (
//                   <motion.div
//                     key="success"
//                     variants={
//                       alertVariants
//                     }
//                     initial="hidden"
//                     animate="visible"
//                     exit="exit"
//                     layout
//                     role="status"
//                     aria-live="polite"
//                     className="
//                       flex
//                       items-start
//                       gap-3
//                       rounded-xl
//                       border
//                       border-emerald-200
//                       bg-emerald-50
//                       p-3.5
//                       text-sm
//                       text-emerald-700
//                     "
//                   >
//                     <FaCheckCircle
//                       className="
//                         mt-0.5
//                         shrink-0
//                       "
//                       aria-hidden="true"
//                     />

//                     <span>
//                       {successMessage}
//                     </span>
//                   </motion.div>
//                 )}

//                 {/* =================================================
//                     LOGIN ERROR / INFORMATION
//                     ================================================= */}

//                 {infoMessage && (
//                   <motion.div
//                     key="info"
//                     variants={
//                       alertVariants
//                     }
//                     initial="hidden"
//                     animate="visible"
//                     exit="exit"
//                     layout
//                     role="alert"
//                     aria-live="assertive"
//                     className="
//                       flex
//                       items-start
//                       gap-3
//                       rounded-xl
//                       border
//                       border-red-200
//                       bg-red-50
//                       p-3.5
//                       text-sm
//                       text-red-700
//                     "
//                   >
//                     <FaExclamationCircle
//                       className="
//                         mt-0.5
//                         shrink-0
//                       "
//                       aria-hidden="true"
//                     />

//                     <span>
//                       {infoMessage}
//                     </span>
//                   </motion.div>
//                 )}

//               </AnimatePresence>
//             </div>

//             {/* =================================================
//                 FORM
//                 ================================================= */}

//             <motion.form
//               onSubmit={handleSubmit}
//               variants={
//                 formContainerVariants
//               }
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : "hidden"
//               }
//               animate="visible"
//               className="
//                 mt-5
//                 space-y-4
//                 sm:mt-8
//                 sm:space-y-5
//               "
//             >

//               {/* =================================================
//                   LOGIN AS
//                   ================================================= */}

//               <motion.div
//                 variants={
//                   formItemVariants
//                 }
//               >
//                 <label
//                   className="
//                     mb-2
//                     block
//                     text-sm
//                     font-semibold
//                     text-[#263B4D]
//                   "
//                 >
//                   Login as
//                 </label>

//                 <div
//                   className="
//                     grid
//                     grid-cols-2
//                     gap-1
//                     rounded-xl
//                     border
//                     border-slate-200
//                     bg-slate-100
//                     p-1
//                   "
//                 >

//                   {/* PATIENT */}

//                   <motion.button
//                     type="button"
//                     disabled={loading}
//                     onClick={() =>
//                       handleRoleChange(
//                         "user"
//                       )
//                     }
//                     whileHover={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             scale: 1.01,
//                           }
//                     }
//                     whileTap={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             scale: 0.98,
//                           }
//                     }
//                     className={`
//                       flex
//                       min-h-[46px]
//                       items-center
//                       justify-center
//                       gap-2
//                       rounded-lg
//                       px-3
//                       py-2
//                       text-sm
//                       font-semibold
//                       transition-colors
//                       duration-200

//                       ${
//                         role ===
//                         "user"
//                           ? `
//                             bg-white
//                             text-[#08A8E8]
//                             shadow-sm
//                           `
//                           : `
//                             text-slate-500
//                             hover:text-[#102F49]
//                           `
//                       }
//                     `}
//                   >
//                     <FaUserInjured />

//                     <span>
//                       Patient
//                     </span>
//                   </motion.button>

//                   {/* DOCTOR */}

// <motion.button
//   type="button"
//   disabled={loading}
//   onClick={() => handleRoleChange("business")}
//   whileHover={
//     shouldReduceMotion
//       ? {}
//       : {
//           scale: 1.01,
//         }
//   }
//   whileTap={
//     shouldReduceMotion
//       ? {}
//       : {
//           scale: 0.98,
//         }
//   }
//   className={`
//     flex
//     min-h-[46px]
//     flex-1
//     items-center
//     justify-center
//     gap-2
//     rounded-lg
//     px-3
//     py-2
//     text-center
//     text-sm
//     font-semibold
//     transition-colors
//     duration-200
//     disabled:cursor-not-allowed
//     disabled:opacity-60

//     ${
//       role === "business"
//         ? `
//           bg-white
//           text-[#08A8E8]
//           shadow-sm
//         `
//         : `
//           text-slate-500
//           hover:text-[#102F49]
//         `
//     }
//   `}
// >
//   <FaUserMd className="shrink-0" />

//   <span className="leading-tight">
//     Doctor / Other Medical / Alternative Medical
//   </span>
// </motion.button>


//                 </div>
//               </motion.div>

//               {/* =================================================
//                   USERNAME
//                   ================================================= */}

//               <motion.div
//                 variants={
//                   formItemVariants
//                 }
//               >
//                 <label
//                   htmlFor="username"
//                   className="
//                     mb-2
//                     block
//                     text-sm
//                     font-semibold
//                     text-[#263B4D]
//                   "
//                 >
//                   Username or Email
//                 </label>

//                 <div className="relative">

//                   <FaUser
//                     className="
//                       pointer-events-none
//                       absolute
//                       left-4
//                       top-1/2
//                       -translate-y-1/2
//                       text-slate-400
//                     "
//                     aria-hidden="true"
//                   />

//                   <motion.input
//                     id="username"
//                     type="text"
//                     value={username}
//                     onChange={(e) => {
//                       setUsername(
//                         e.target.value
//                       );

//                       dispatch(
//                         clearError()
//                       );

//                       setInfoMessage("");
//                     }}
//                     placeholder="Enter username or email"
//                     autoComplete="username"
//                     disabled={loading}
//                     whileFocus={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             scale: 1.005,
//                           }
//                     }
//                     className="
//                       h-12
//                       w-full
//                       rounded-xl
//                       border
//                       border-slate-200
//                       bg-white
//                       pl-11
//                       pr-4
//                       text-sm
//                       text-[#102F49]
//                       outline-none
//                       transition-all
//                       placeholder:text-slate-400
//                       hover:border-slate-300
//                       focus:border-[#08A8E8]
//                       focus:ring-4
//                       focus:ring-[#08A8E8]/10
//                       disabled:cursor-not-allowed
//                       disabled:opacity-60
//                       sm:h-[52px]
//                       sm:text-base
//                     "
//                   />

//                 </div>
//               </motion.div>

//               {/* =================================================
//                   PASSWORD
//                   ================================================= */}

//               <motion.div
//                 variants={
//                   formItemVariants
//                 }
//               >

//                 <div
//                   className="
//                     mb-2
//                     flex
//                     items-center
//                     justify-between
//                     gap-3
//                   "
//                 >

//                   <label
//                     htmlFor="password"
//                     className="
//                       text-sm
//                       font-semibold
//                       text-[#263B4D]
//                     "
//                   >
//                     Password
//                   </label>

//                   <motion.button
//                     type="button"
//                     disabled={loading}
//                     onClick={() =>
//                       navigate(
//                         "/forgot-password"
//                       )
//                     }
//                     whileHover={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             x: 2,
//                           }
//                     }
//                     className="
//                       text-xs
//                       font-semibold
//                       text-[#079BD5]
//                       transition
//                       hover:text-[#087DAE]
//                       hover:underline
//                       disabled:opacity-50
//                       sm:text-sm
//                     "
//                   >
//                     Forgot Password?
//                   </motion.button>

//                 </div>

//                 <div className="relative">

//                   <FaLock
//                     className="
//                       pointer-events-none
//                       absolute
//                       left-4
//                       top-1/2
//                       -translate-y-1/2
//                       text-slate-400
//                     "
//                     aria-hidden="true"
//                   />

//                   <motion.input
//                     id="password"
//                     type={
//                       showPassword
//                         ? "text"
//                         : "password"
//                     }
//                     value={password}
//                     onChange={(e) => {
//                       setPassword(
//                         e.target.value
//                       );

//                       dispatch(
//                         clearError()
//                       );

//                       setInfoMessage("");
//                     }}
//                     placeholder="Enter your password"
//                     autoComplete="current-password"
//                     disabled={loading}
//                     whileFocus={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             scale: 1.005,
//                           }
//                     }
//                     className="
//                       h-12
//                       w-full
//                       rounded-xl
//                       border
//                       border-slate-200
//                       bg-white
//                       pl-11
//                       pr-12
//                       text-sm
//                       text-[#102F49]
//                       outline-none
//                       transition-all
//                       placeholder:text-slate-400
//                       hover:border-slate-300
//                       focus:border-[#08A8E8]
//                       focus:ring-4
//                       focus:ring-[#08A8E8]/10
//                       disabled:cursor-not-allowed
//                       disabled:opacity-60
//                       sm:h-[52px]
//                       sm:text-base
//                     "
//                   />

//                   <motion.button
//                     type="button"
//                     aria-label={
//                       showPassword
//                         ? "Hide password"
//                         : "Show password"
//                     }
//                     disabled={loading}
//                     onClick={() =>
//                       setShowPassword(
//                         (prev) =>
//                           !prev
//                       )
//                     }
//                     whileHover={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             scale: 1.08,
//                           }
//                     }
//                     whileTap={
//                       shouldReduceMotion
//                         ? {}
//                         : {
//                             scale: 0.92,
//                           }
//                     }
//                     className="
//                       absolute
//                       right-2
//                       top-1/2
//                       flex
//                       h-9
//                       w-9
//                       -translate-y-1/2
//                       items-center
//                       justify-center
//                       rounded-lg
//                       text-slate-400
//                       transition
//                       hover:bg-slate-100
//                       hover:text-[#102F49]
//                       disabled:opacity-50
//                     "
//                   >
//                     {showPassword ? (
//                       <FaEyeSlash />
//                     ) : (
//                       <FaEye />
//                     )}
//                   </motion.button>

//                 </div>
//               </motion.div>

//               {/* =================================================
//                   LOGIN BUTTON
//                   ================================================= */}

//               <motion.button
//                 type="submit"
//                 disabled={loading}
//                 variants={
//                   formItemVariants
//                 }
//                 whileHover={
//                   loading ||
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         y: -2,
//                         scale: 1.01,
//                       }
//                 }
//                 whileTap={
//                   loading ||
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         scale: 0.98,
//                       }
//                 }
//                 className={`
//                   group
//                   flex
//                   min-h-[52px]
//                   w-full
//                   items-center
//                   justify-center
//                   gap-2
//                   rounded-xl
//                   px-5
//                   text-sm
//                   font-bold
//                   text-white
//                   transition-colors
//                   duration-200
//                   sm:text-base

//                   ${
//                     loading
//                       ? `
//                         cursor-not-allowed
//                         bg-slate-400
//                       `
//                       : `
//                         bg-[#08A8E8]
//                         shadow-lg
//                         shadow-[#08A8E8]/20
//                         hover:bg-[#079BD5]
//                         hover:shadow-xl
//                         hover:shadow-[#08A8E8]/25
//                       `
//                   }
//                 `}
//               >

//                 {loading ? (
//                   <>
//                     <motion.span
//                       animate={
//                         shouldReduceMotion
//                           ? {}
//                           : {
//                               rotate: 360,
//                             }
//                       }
//                       transition={{
//                         duration: 1.2,
//                         repeat:
//                           Infinity,
//                         ease: "linear",
//                       }}
//                       className="
//                         h-5
//                         w-5
//                         rounded-full
//                         border-2
//                         border-white/30
//                         border-t-white
//                       "
//                       aria-hidden="true"
//                     />

//                     <span>
//                       Logging in...
//                     </span>
//                   </>
//                 ) : (
//                   <>
//                     <span>
//                       Login to MRHealth
//                     </span>

//                     <motion.span
//                       whileHover={
//                         shouldReduceMotion
//                           ? {}
//                           : {
//                               x: 5,
//                             }
//                       }
//                     >
//                       <FaArrowRight />
//                     </motion.span>
//                   </>
//                 )}

//               </motion.button>

//               {/* =================================================
//                   DIVIDER
//                   ================================================= */}

//               <motion.div
//                 variants={
//                   formItemVariants
//                 }
//                 className="
//                   relative
//                   py-1
//                   sm:py-2
//                 "
//               >
//                 <div
//                   className="
//                     absolute
//                     inset-0
//                     flex
//                     items-center
//                   "
//                 >
//                   <div
//                     className="
//                       w-full
//                       border-t
//                       border-slate-200
//                     "
//                   />
//                 </div>

//                 <div
//                   className="
//                     relative
//                     flex
//                     justify-center
//                   "
//                 >
//                   <span
//                     className="
//                       bg-[#F7FAFC]
//                       px-3
//                       text-xs
//                       text-slate-400
//                     "
//                   >
//                     New to MRHealth?
//                   </span>
//                 </div>
//               </motion.div>

//               {/* =================================================
//                   PATIENT ACCOUNT
//                   ================================================= */}

//               <motion.button
//                 type="button"
//                 disabled={loading}
//                 onClick={() =>
//                   navigate(
//                     "/sign-up"
//                   )
//                 }
//                 variants={
//                   formItemVariants
//                 }
//                 whileHover={
//                   loading ||
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         y: -2,
//                         scale: 1.01,
//                       }
//                 }
//                 whileTap={
//                   loading ||
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         scale: 0.98,
//                       }
//                 }
//                 className="
//                   flex
//                   min-h-[46px]
//                   w-full
//                   items-center
//                   justify-center
//                   gap-2
//                   rounded-xl
//                   border
//                   border-[#B8E8F6]
//                   bg-[#EFFAFF]
//                   px-4
//                   text-sm
//                   font-bold
//                   text-[#078EBF]
//                   transition-colors
//                   hover:border-[#8DDCF1]
//                   hover:bg-[#E3F8FD]
//                   disabled:cursor-not-allowed
//                   disabled:opacity-50
//                   sm:min-h-[48px]
//                   sm:text-base
//                 "
//               >
//                 <FaUserPlus />

//                 <span>
//                   Create Patient Account
//                 </span>
//               </motion.button>

//               {/* =================================================
//                   DOCTOR REGISTRATION
//                   ================================================= */}

//               <motion.button
//                 type="button"
//                 disabled={loading}
//                 onClick={() =>
//                   navigate(
//                     "/RegistrationForm"
//                   )
//                 }
//                 variants={
//                   formItemVariants
//                 }
//                 whileHover={
//                   loading ||
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         y: -2,
//                         scale: 1.01,
//                       }
//                 }
//                 whileTap={
//                   loading ||
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         scale: 0.98,
//                       }
//                 }
//                 className="
//                   flex
//                   min-h-[46px]
//                   w-full
//                   items-center
//                   justify-center
//                   gap-2
//                   rounded-xl
//                   border
//                   border-[#C8D9E5]
//                   bg-white
//                   px-4
//                   text-sm
//                   font-bold
//                   text-[#24465E]
//                   transition-colors
//                   hover:border-[#9FB8C8]
//                   hover:bg-[#F5F9FB]
//                   disabled:cursor-not-allowed
//                   disabled:opacity-50
//                   sm:min-h-[48px]
//                   sm:text-base
//                 "
//               >
//                 <FaStethoscope />

//                 <span>
//                   Register as Doctor
//                 </span>
//               </motion.button>

//             </motion.form>

//             {/* =================================================
//                 SECURITY
//                 ================================================= */}

//             <motion.div
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : {
//                       opacity: 0,
//                       y: 10,
//                     }
//               }
//               animate={{
//                 opacity: 1,
//                 y: 0,
//               }}
//               transition={{
//                 duration: 0.7,
//                 delay: 1.1,
//               }}
//               className="
//                 mt-5
//                 flex
//                 items-center
//                 justify-center
//                 gap-2
//                 text-center
//                 text-[11px]
//                 text-slate-400
//                 sm:mt-6
//                 sm:text-xs
//               "
//             >
//               <motion.div
//                 animate={
//                   shouldReduceMotion
//                     ? {}
//                     : {
//                         scale: [
//                           1,
//                           1.12,
//                           1,
//                         ],
//                       }
//                 }
//                 transition={{
//                   duration: 4,
//                   repeat: Infinity,
//                   ease: "easeInOut",
//                 }}
//               >
//                 <FaShieldAlt
//                   className="
//                     shrink-0
//                     text-emerald-500
//                   "
//                 />
//               </motion.div>

//               <span>
//                 Your login information is
//                 securely protected.
//               </span>
//             </motion.div>

//             {/* =================================================
//                 MOBILE FOOTER
//                 ================================================= */}

//             <motion.div
//               initial={
//                 shouldReduceMotion
//                   ? false
//                   : {
//                       opacity: 0,
//                     }
//               }
//               animate={{
//                 opacity: 1,
//               }}
//               transition={{
//                 duration: 0.8,
//                 delay: 1.2,
//               }}
//               className="
//                 mt-4
//                 text-center
//                 text-[11px]
//                 text-slate-400
//                 sm:mt-5
//                 lg:hidden
//               "
//             >
//               ©{" "}
//               {new Date().getFullYear()}{" "}
//               MRHealth
//               {" · "}mrhealth.co.in
//             </motion.div>

//           </div>
//         </motion.section>
//       </div>
//     </main>
//   );
// };

// export default Login;

